// M12 - MotricityRecentCard
//
// Card en el dashboard general del terapeuta con las ultimas 10
// sesiones de motricidad VR que han llegado (status completed/partial).
// El listado del backend popula patient.name/surname.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Table, Tag, Empty, Skeleton, Avatar, Typography } from "antd";
import {
  AppstoreOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import { ReviMotricityApi } from "../../../../api/revi/reviMotricityApi";
import { useAuth } from "../../../../hooks";
import { useShellEstrecho } from "../../../../hooks/useIsNarrow";

const { Text } = Typography;
const api = new ReviMotricityApi();

function StatusTag({ status }) {
  const { t } = useTranslation("motricity");

  if (status === "completed") {
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        {t("recentCard.status.completed")}
      </Tag>
    );
  }
  if (status === "partial") {
    return (
      <Tag color="orange" icon={<CheckCircleOutlined />}>
        {t("recentCard.status.partial")}
      </Tag>
    );
  }
  return (
    <Tag color="gold" icon={<ClockCircleOutlined />}>
      {status}
    </Tag>
  );
}

export function MotricityRecentCard() {
  const shellEstrecho = useShellEstrecho();
  // Dos namespaces: el propio del area y el comun, del que salen las
  // cabeceras "Fecha", "Paciente" y "Estado" que comparte todo el portal.
  const { t } = useTranslation(["motricity", "common"]);
  // Fecha y duracion siguen al idioma de la interfaz.
  const { fechaHora, duracion } = useFormatos();
  const { accessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.listSessions(accessToken, {
        limit: 10,
        status: "completed",
      });
      setItems(res?.items || []);
    } catch (e) {
      setError(e?.body?.message || e?.message || t("recentCard.genericError"));
    } finally {
      setLoading(false);
    }
  }, [accessToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const hayPuntuacion = useMemo(
    () => items.some((s) => typeof s?.derived?.core?.score === "number"),
    [items],
  );

  const columns = useMemo(
    () => [
      {
        title: t("common:fields.date"),
        dataIndex: "startedAt",
        key: "startedAt",
        render: (v, r) => fechaHora(v || r.createdAt),
      },
      {
        title: t("common:roles.patient"),
        key: "patient",
        render: (_v, r) => {
          const info = r.patientInfo;
          if (!info) return <Text type="secondary">—</Text>;
          return (
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Avatar
                src={info.avatar || undefined}
                icon={!info.avatar ? <UserOutlined /> : undefined}
                size={24}
              />
              <span>
                {info.name} {info.surname}
              </span>
            </span>
          );
        },
      },
      {
        title: t("recentCard.columns.game"),
        dataIndex: "gameKey",
        key: "gameKey",
        render: (v) => <Tag>{v}</Tag>,
      },
      {
        title: t("common:fields.status"),
        dataIndex: "status",
        key: "status",
        render: (s) => <StatusTag status={s} />,
      },
      // OJO: esta columna hacia `?? 0`, y por tanto pintaba un CERO en la
      // columna "Errores" para toda sesion que no los midiera. Cero errores y
      // "aqui no se miden errores" son cosas opuestas para un terapeuta, y en
      // una tabla del dashboard esa diferencia no se puede recuperar mirando.
      {
        title: t("recentCard.columns.faults"),
        key: "faults",
        render: (_v, r) => {
          const errores = r.derived?.core
            ? r.derived.core.errors
            : r.summary?.totalFaults;
          if (typeof errores !== "number" || !Number.isFinite(errores)) {
            return <Text type="secondary">—</Text>;
          }
          return errores;
        },
      },
      // Solo aparece si ALGUNA sesion trae puntuacion. Una columna entera de
      // guiones no informa de nada.
      ...(hayPuntuacion
        ? [
            {
              title: t("recentCard.columns.puntuacion"),
              key: "puntuacion",
              render: (_v, r) => {
                const s = r.derived?.core?.score;
                if (typeof s !== "number") {
                  return <Text type="secondary">—</Text>;
                }
                return s;
              },
            },
          ]
        : []),
      {
        title: t("recentCard.columns.duration"),
        dataIndex: "durationSeconds",
        key: "duration",
        render: (v) => duracion(v),
      },
    ],
    [t, fechaHora, duracion, hayPuntuacion],
  );

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <AppstoreOutlined />
          {t("recentCard.title")}
        </span>
      }
      style={{ marginTop: 16 }}
      variant="outlined"
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : error ? (
        <Text type="danger">{error}</Text>
      ) : items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("recentCard.empty")}
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          dataSource={items}
          columns={columns}
          pagination={false}
          /*
            Seis columnas no caben en 375 px y la tabla desbordaba su tarjeta
            hacia la derecha, fuera de la pantalla. Con esto se desplaza por
            dentro.

            Solo bajo 992: al activar el scroll, rc-table inyecta una fila de
            medida en el cuerpo, y hay un test que indexa las celdas por
            posicion.
          */
          scroll={shellEstrecho ? { x: "max-content" } : undefined}
        />
      )}
    </Card>
  );
}
